'use client';

/**
 * SALLE ROYALE — Salle de visioconférence NDIAMSYL PEYE.
 * Pré-salle (nom, aperçu caméra, micro/caméra) → salle : grille vidéo
 * (caméra locale réelle via getUserMedia + participants royaux simulés),
 * barre de contrôle, chat latéral, chrono de session, main levée.
 * Repli élégant si la caméra est refusée ou indisponible.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { cn } from '@/lib/utils';
import {
  Video, VideoOff, Mic, MicOff, MonitorUp, Hand, PhoneOff, Send, Users,
  MessageSquare, Crown, CalendarDays,
} from 'lucide-react';

interface ChatMsg { author: string; text: string; self?: boolean; time: string }

/** Participants royaux de la session (mémoire de la salle). */
const PEERS = [
  { name: 'Seydina M. (Dakar)', hue: 160, role: 'Foriji' },
  { name: 'Fatou S. (Touba)', hue: 45, role: 'Digdaum' },
  { name: 'Cheikh N. (Kaolack)', hue: 200, role: 'Hifz' },
  { name: 'Maryam B. (Paris)', hue: 275, role: 'Kang' },
  { name: 'Imran D. (Medina)', hue: 20, role: 'Foriji' },
];

const now = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export default function ConferenceSection() {
  const { uiLang, member } = useApp();
  const t = DICTS[uiLang];

  const [name, setName] = useState('');
  const [joined, setJoined] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [handUp, setHandUp] = useState(false);
  const [camError, setCamError] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [draft, setDraft] = useState('');
  const [showChat, setShowChat] = useState(true);
  const [speakerIdx, setSpeakerIdx] = useState(-1);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  // Synchronisation unique du nom depuis le profil membre (ajustement pendant le rendu)
  const [nameSynced, setNameSynced] = useState(false);
  if (!nameSynced) {
    setNameSynced(true);
    if (member?.name) setName(member.name);
  }

  /* Aperçu caméra (pré-salle + salle) */
  const startCam = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = s;
      setCamError(false);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        void videoRef.current.play().catch(() => undefined);
      }
    } catch {
      setCamError(true);
      setCamOn(false);
    }
  }, []);

  const stopCam = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (camOn) {
      // Diffère l'ouverture de la caméra hors du corps d'effet (règle react-hooks)
      const id = setTimeout(() => { void startCam(); }, 0);
      return () => { clearTimeout(id); stopCam(); };
    }
    stopCam();
    return undefined;
  }, [camOn, startCam, stopCam]);

  /* Horloge de session */
  useEffect(() => {
    if (!joined) return;
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [joined]);

  /* Tour de parole simulé des participants */
  useEffect(() => {
    if (!joined) return;
    const id = setInterval(() => {
      setSpeakerIdx(Math.floor(Math.random() * PEERS.length));
    }, 4200);
    return () => clearInterval(id);
  }, [joined]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, showChat]);

  const mmss = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const join = () => {
    const finalName = name.trim() || (member?.name ?? 'Invité Royal');
    setJoined(true);
    setElapsed(0);
    setMessages([
      { author: 'Salle Royale', text: t.conference.welcome, time: now() },
      { author: 'Fatou S. (Touba)', text: t.conference.greeting, time: now() },
    ]);
    setCamOn(true);
    void finalName;
  };

  const leave = () => {
    setJoined(false);
    setSharing(false);
    setHandUp(false);
    setSpeakerIdx(-1);
    stopCam();
  };

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    setMessages((m) => [...m, { author: name.trim() || 'Moi', text, self: true, time: now() }]);
    setDraft('');
  };

  const onChatKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  /* ============ PRÉ-SALLE ============ */
  if (!joined) {
    return (
      <section className="relative min-h-screen py-12" aria-label={t.conference.title}>
        {/* Bannière dorée — Salle Royale */}
        <PageBanner
          src="/images/banners/conference_banner.png"
          eyebrow={t.conference.eyebrow}
          title={t.conference.title}
          sub={t.conference.subtitle}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 55% 42% at 50% 0%, rgba(212,175,55,0.13), transparent 62%), radial-gradient(ellipse 42% 30% at 85% 82%, rgba(63,143,168,0.10), transparent 70%)',
          }}
        />
        <div className="relative mx-auto max-w-3xl px-4 pt-10 sm:px-6">
          <div className="glass-gold rounded-2xl p-6 sm:p-8">
            {/* Aperçu caméra */}
            <div className="video-tile relative mx-auto mb-6 aspect-video w-full max-w-xl rounded-2xl">
              {camOn && !camError ? (
                <video ref={videoRef} muted playsInline autoPlay
                  className="h-full w-full scale-x-[-1] object-cover" />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3">
                  {camError ? (
                    <>
                      <VideoOff className="h-10 w-10 text-[#d4af37]/50" aria-hidden />
                      <p className="max-w-xs text-center text-xs text-[#cfe0cf]/60">{t.conference.cam_error}</p>
                    </>
                  ) : (
                    <Video className="h-10 w-10 text-[#d4af37]/40" aria-hidden />
                  )}
                  <span className="star-ornament h-8 w-8 bg-gradient-to-br from-[#f7e7b0] via-[#d4af37] to-[#9a7b1e]" aria-hidden />
                </div>
              )}
              <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-[rgba(2,25,19,0.8)] px-2.5 py-1 text-[10px] font-bold text-[#f0d98c]">
                <Crown className="h-3 w-3" aria-hidden /> {name.trim() || 'Moi'} — {t.conference.preview}
              </span>
            </div>

            {/* Nom + toggles */}
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-[#cfe0cf]/70">{t.conference.your_name}</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.conference.name_placeholder}
                className="w-full rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(2,25,19,0.8)] px-4 py-3 text-sm text-[#f5f0e1] placeholder:text-[#cfe0cf]/40 outline-none focus:border-[#d4af37]"
                aria-label={t.conference.your_name}
              />
            </label>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => setMicOn(!micOn)}
                className={cn('flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-all',
                  micOn ? 'border-[#2e8b6e] bg-[rgba(46,139,110,0.15)] text-[#7fd6b4]' : 'border-[#e05252]/60 bg-[rgba(224,82,82,0.12)] text-[#e08a8a]')}
                aria-pressed={micOn}
              >
                {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                {micOn ? t.conference.mic_on : t.conference.mic_off}
              </button>
              <button
                onClick={() => setCamOn(!camOn)}
                className={cn('flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-all',
                  camOn ? 'border-[#2e8b6e] bg-[rgba(46,139,110,0.15)] text-[#7fd6b4]' : 'border-[#e05252]/60 bg-[rgba(224,82,82,0.12)] text-[#e08a8a]')}
                aria-pressed={camOn}
              >
                {camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                {camOn ? t.conference.cam_on : t.conference.cam_off}
              </button>
            </div>

            <button
              onClick={join}
              className="gold-glow-hover mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#e8c86a] to-[#b8933a] px-6 py-3.5 text-sm font-bold text-[#032019]"
            >
              <Video className="h-4 w-4" aria-hidden />
              {t.conference.join}
            </button>
            <p className="mt-3 text-center text-[11px] text-[#cfe0cf]/50">{t.conference.peers_note}</p>
          </div>
        </div>

        {/* ===== Programme Royal — contenu complet de la Salle Royale ===== */}
        <div className="mx-auto mt-14 max-w-5xl px-4 sm:px-6">
          <div className="ornament-divider mb-6">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.1)]">
              <CalendarDays className="h-4 w-4 text-[#d4af37]" aria-hidden />
            </span>
          </div>
          <div className="mb-7 text-center">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.34em] text-[#d4af37]/80">{t.program.eyebrow}</p>
            <h2 className="font-serif-royal text-2xl font-bold sm:text-3xl">
              <span className="text-gold-gradient">{t.program.title}</span>
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-[#cfe0cf]/75">{t.program.sub}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {(Object.keys(t.program.days) as (keyof typeof t.program.days)[]).map((d, i) => {
              const it = t.program.items[d];
              return (
                <article
                  key={d}
                  className="glass-gold glass-gold-hover relative overflow-hidden rounded-2xl p-5"
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <span aria-hidden className="pointer-events-none absolute -right-3 -top-5 font-serif-royal text-[72px] font-bold leading-none text-[rgba(212,175,55,0.06)]">
                    {i + 1}
                  </span>
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#d4af37]/85">{t.program.days[d]}</p>
                  <h3 className="mt-1.5 text-[15px] font-bold text-[#f5f0e1]">{it.name}</h3>
                  <p className="mt-1 text-[11px] font-semibold text-[#7fd6b4]">{it.time}</p>
                  <p className="mt-2 text-[12.5px] leading-relaxed text-[#cfe0cf]/70">{it.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  /* ============ SALLE ACTIVE ============ */
  const meSpeaking = false; // l'indicateur local s'allume quand le micro parle (démo)

  return (
    <section className="relative min-h-screen py-8" aria-label={t.conference.title}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        {/* Bandeau salle */}
        <div className="glass-gold mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.12)]">
              <Crown className="h-4 w-4 text-[#d4af37]" aria-hidden />
            </span>
            <div>
              <p className="text-sm font-bold text-[#f0d98c]">{t.conference.room_name}</p>
              <p className="text-[11px] text-[#cfe0cf]/60">NDIAMSYL PEYE · {t.conference.session} {mmss(elapsed)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 rounded-full border border-[rgba(46,139,110,0.5)] bg-[rgba(46,139,110,0.15)] px-3 py-1 text-[11px] font-bold text-[#7fd6b4]">
              <Users className="h-3.5 w-3.5" aria-hidden /> {PEERS.length + 1} {t.conference.participants}
            </span>
            {handUp && (
              <span className="gold-pulse flex items-center gap-1.5 rounded-full border border-[#d4af37] bg-[rgba(212,175,55,0.18)] px-3 py-1 text-[11px] font-bold text-[#f0d98c]">
                <Hand className="h-3.5 w-3.5" aria-hidden /> {t.conference.hand_up}
              </span>
            )}
            <button
              onClick={() => setShowChat(!showChat)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-[rgba(212,175,55,0.35)] text-[#f0d98c] transition-colors hover:bg-[rgba(212,175,55,0.12)]"
              aria-label={t.conference.chat}
              aria-pressed={showChat}
            >
              <MessageSquare className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>

        <div className={cn('grid gap-4', showChat ? 'lg:grid-cols-[1fr_320px]' : '')}>
          {/* ===== Grille vidéo ===== */}
          <div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {/* Tuile locale */}
              <div className={cn('video-tile relative aspect-video rounded-xl', sharing && 'ring-2 ring-[#d4af37]')}>
                {sharing ? (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[linear-gradient(150deg,rgba(212,175,55,0.14),rgba(2,25,19,0.95))]">
                    <Crown className="h-10 w-10 text-[#d4af37]" aria-hidden />
                    <p className="text-xs font-bold tracking-widest text-[#f0d98c] uppercase">{t.conference.screen_share}</p>
                    <p className="text-[10px] text-[#cfe0cf]/55">{t.conference.screen_share_note}</p>
                  </div>
                ) : camOn && !camError ? (
                  <video ref={videoRef} muted playsInline autoPlay className="h-full w-full scale-x-[-1] object-cover" />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2">
                    <VideoOff className="h-8 w-8 text-[#d4af37]/45" aria-hidden />
                    <span className="text-xs font-bold text-[#cfe0cf]/70">{name.trim() || 'Moi'}</span>
                  </div>
                )}
                <span className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-[rgba(2,25,19,0.85)] px-2 py-0.5 text-[10px] font-bold text-[#f0d98c]">
                  {micOn ? <Mic className="h-3 w-3 text-[#7fd6b4]" /> : <MicOff className="h-3 w-3 text-[#e08a8a]" />}
                  {name.trim() || 'Moi'} {sharing && `· ${t.conference.sharing}`}
                </span>
              </div>

              {/* Participants royaux */}
              {PEERS.map((p, i) => {
                const speaking = speakerIdx === i;
                return (
                  <div key={p.name} className={cn('video-tile relative aspect-video rounded-xl', speaking && 'video-tile-speaking')}>
                    <div className="flex h-full w-full items-center justify-center"
                      style={{ background: `radial-gradient(circle at 50% 35%, hsl(${p.hue} 40% 16%), rgba(2,25,19,0.98) 75%)` }}>
                      <span
                        className={cn('flex h-16 w-16 items-center justify-center rounded-full border-2 font-serif-royal text-xl font-bold text-[#f0d98c] transition-all',
                          speaking ? 'scale-110' : 'opacity-85')}
                        style={{ borderColor: speaking ? '#d4af37' : 'rgba(212,175,55,0.35)', background: `hsl(${p.hue} 45% 12%)` }}
                        aria-hidden
                      >
                        {p.name.charAt(0)}
                      </span>
                      {speaking && (
                        <span className="gold-pulse absolute bottom-4 flex gap-0.5" aria-hidden>
                          {[0, 1, 2].map((b) => (
                            <span key={b} className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" style={{ animationDelay: `${b * 0.15}s` }} />
                          ))}
                        </span>
                      )}
                    </div>
                    <span className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-[rgba(2,25,19,0.85)] px-2 py-0.5 text-[10px] font-bold text-[#f0d98c]">
                      <Mic className="h-3 w-3 text-[#7fd6b4]" aria-hidden />
                      {p.name}
                      <span className="text-[9px] font-semibold uppercase text-[#cfe0cf]/55">{p.role}</span>
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Barre de contrôle */}
            <div className="glass-gold mt-4 flex flex-wrap items-center justify-center gap-2.5 rounded-2xl px-4 py-3.5">
              <button
                onClick={() => setMicOn(!micOn)}
                className={cn('flex h-11 items-center gap-2 rounded-xl border px-4 text-xs font-bold transition-all',
                  micOn ? 'border-[#2e8b6e]/60 bg-[rgba(46,139,110,0.15)] text-[#7fd6b4]' : 'border-[#e05252]/60 bg-[rgba(224,82,82,0.12)] text-[#e08a8a]')}
                aria-pressed={micOn}
                aria-label={micOn ? t.conference.mic_off : t.conference.mic_on}
              >
                {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                <span className="hidden sm:inline">{micOn ? t.conference.mic_on : t.conference.mic_off}</span>
              </button>
              <button
                onClick={() => setCamOn(!camOn)}
                className={cn('flex h-11 items-center gap-2 rounded-xl border px-4 text-xs font-bold transition-all',
                  camOn ? 'border-[#2e8b6e]/60 bg-[rgba(46,139,110,0.15)] text-[#7fd6b4]' : 'border-[#e05252]/60 bg-[rgba(224,82,82,0.12)] text-[#e08a8a]')}
                aria-pressed={camOn}
                aria-label={camOn ? t.conference.cam_off : t.conference.cam_on}
              >
                {camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                <span className="hidden sm:inline">{camOn ? t.conference.cam_on : t.conference.cam_off}</span>
              </button>
              <button
                onClick={() => setSharing(!sharing)}
                className={cn('flex h-11 items-center gap-2 rounded-xl border px-4 text-xs font-bold transition-all',
                  sharing ? 'border-[#d4af37] bg-[rgba(212,175,55,0.18)] text-[#f0d98c]' : 'border-[rgba(212,175,55,0.35)] text-[#cfe0cf]/80 hover:text-[#f0d98c]')}
                aria-pressed={sharing}
              >
                <MonitorUp className="h-4 w-4" />
                <span className="hidden sm:inline">{sharing ? t.conference.stop_share : t.conference.share}</span>
              </button>
              <button
                onClick={() => setHandUp(!handUp)}
                className={cn('flex h-11 items-center gap-2 rounded-xl border px-4 text-xs font-bold transition-all',
                  handUp ? 'gold-pulse border-[#d4af37] bg-[rgba(212,175,55,0.18)] text-[#f0d98c]' : 'border-[rgba(212,175,55,0.35)] text-[#cfe0cf]/80 hover:text-[#f0d98c]')}
                aria-pressed={handUp}
              >
                <Hand className="h-4 w-4" />
                <span className="hidden sm:inline">{t.conference.hand}</span>
              </button>
              <button
                onClick={leave}
                className="flex h-11 items-center gap-2 rounded-xl border border-[#e05252]/70 bg-[rgba(224,82,82,0.16)] px-5 text-xs font-bold text-[#ffb3b3] transition-all hover:bg-[rgba(224,82,82,0.28)]"
              >
                <PhoneOff className="h-4 w-4" />
                {t.conference.leave}
              </button>
            </div>
          </div>

          {/* ===== Chat latéral ===== */}
          {showChat && (
            <aside className="glass-gold flex max-h-[560px] flex-col rounded-2xl lg:max-h-[calc(100vh-190px)]" aria-label={t.conference.chat}>
              <p className="border-b border-[rgba(212,175,55,0.25)] px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#d4af37]">
                {t.conference.chat} — {t.conference.room_name}
              </p>
              <div className="flex-1 space-y-3 overflow-y-auto p-4 gold-scroll">
                {messages.map((m, i) => (
                  <div key={i} className={cn('max-w-[92%] rounded-xl px-3 py-2', m.self ? 'ml-auto border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.12)]' : 'border border-[rgba(212,175,55,0.18)] bg-[rgba(2,25,19,0.7)]')}>
                    <p className="flex items-baseline justify-between gap-2 text-[10px] font-bold text-[#d4af37]">
                      {m.author}
                      <span className="font-normal text-[#cfe0cf]/40">{m.time}</span>
                    </p>
                    <p className="mt-0.5 text-[12.5px] leading-snug text-[#e5e0d0]/90">{m.text}</p>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <div className="flex gap-2 border-t border-[rgba(212,175,55,0.25)] p-3">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onChatKey}
                  placeholder={t.conference.chat_placeholder}
                  className="min-w-0 flex-1 rounded-lg border border-[rgba(212,175,55,0.3)] bg-[rgba(2,25,19,0.8)] px-3 py-2 text-sm text-[#f5f0e1] placeholder:text-[#cfe0cf]/40 outline-none focus:border-[#d4af37]"
                  aria-label={t.conference.chat_placeholder}
                />
                <button
                  onClick={send}
                  className="gold-glow-hover flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-b from-[#e8c86a] to-[#b8933a] text-[#032019]"
                  aria-label={t.conference.send}
                >
                  <Send className="h-4 w-4 rtl-flip" aria-hidden />
                </button>
              </div>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}
